// v1.31.42 — Blocked UPI List. Shows every UPI ID flagged as NOT belonging to
// the business. Any slip whose receiver UPI matches a blocked entry is rejected
// at the upload gate. Admins can block a new UPI here or unblock existing ones.
import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ShieldSlash, Trash, Plus } from "@phosphor-icons/react";
import { dmYhm } from "@/lib/dateFmt";

export default function BlockedUpis() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [upi, setUpi] = useState("");
    const [reason, setReason] = useState("");
    const [saving, setSaving] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/blocked-upis");
            setRows(Array.isArray(data) ? data : []);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const block = async () => {
        const id = upi.trim();
        if (!id) { toast.error("Enter a UPI ID to block"); return; }
        setSaving(true);
        try {
            await api.post("/blocked-upis", { upi_id: id, reason: reason.trim() });
            toast.success("UPI blocked");
            setUpi(""); setReason("");
            load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };

    const unblock = async (row) => {
        if (!window.confirm(`Unblock ${row.upi_id}? Slips paid to this UPI will be allowed again.`)) return;
        try {
            await api.post(`/blocked-upis/${row.id}/unblock`);
            toast.success("UPI unblocked");
            setRows((rs) => rs.filter((r) => r.id !== row.id));
        } catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <div data-testid="blocked-upis-page" className="space-y-5">
            <div>
                <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Administration</div>
                <h1 className="mt-1 flex items-center gap-2 font-display text-2xl font-bold text-slate-900">
                    <ShieldSlash size={24} weight="fill" className="text-rose-600" /> Blocked UPI List
                </h1>
                <p className="mt-1 text-sm text-slate-500">
                    UPI IDs flagged as <b>not belonging to us</b>. Any payment slip whose receiver UPI
                    matches a blocked entry is rejected at upload — before OCR — with a message asking
                    the uploader to contact the administrator.
                </p>
            </div>

            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Block a UPI ID</div>
                <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center">
                    <input
                        data-testid="block-upi-id-input"
                        value={upi}
                        onChange={(e) => setUpi(e.target.value)}
                        placeholder="e.g. fraud@okaxis"
                        className="h-9 flex-1 rounded-md border border-slate-300 px-3 font-mono text-sm focus:border-rose-500 focus:outline-none"
                    />
                    <input
                        data-testid="block-upi-reason-input"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="Reason (optional)"
                        className="h-9 flex-1 rounded-md border border-slate-300 px-3 text-sm focus:border-rose-500 focus:outline-none"
                    />
                    <button
                        type="button"
                        data-testid="block-upi-submit"
                        onClick={block}
                        disabled={saving}
                        className="inline-flex h-9 items-center justify-center gap-1 rounded-md bg-rose-600 px-4 text-xs font-bold uppercase tracking-wider text-white transition hover:bg-rose-700 disabled:opacity-50"
                    >
                        <Plus size={14} weight="bold" /> {saving ? "Blocking…" : "Block UPI"}
                    </button>
                </div>
            </div>

            <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
                        <tr>
                            <th className="px-4 py-2.5">UPI ID</th>
                            <th className="px-4 py-2.5">Reason</th>
                            <th className="px-4 py-2.5">Blocked By</th>
                            <th className="px-4 py-2.5">When</th>
                            <th className="px-4 py-2.5 text-right">Action</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100" data-testid="blocked-upis-tbody">
                        {loading ? (
                            <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400">Loading…</td></tr>
                        ) : rows.length === 0 ? (
                            <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400" data-testid="blocked-upis-empty">No blocked UPI IDs.</td></tr>
                        ) : rows.map((r) => (
                            <tr key={r.id} data-testid={`blocked-upi-row-${r.id}`} className="hover:bg-slate-50">
                                <td className="px-4 py-2.5 font-mono font-semibold text-slate-900">{r.upi_id}</td>
                                <td className="px-4 py-2.5 text-slate-600">{r.reason || <span className="italic text-slate-400">—</span>}</td>
                                <td className="px-4 py-2.5 text-slate-600">{r.blocked_by || "—"}</td>
                                <td className="px-4 py-2.5 text-slate-500">{r.created_at ? dmYhm(r.created_at) : "—"}</td>
                                <td className="px-4 py-2.5 text-right">
                                    <button
                                        type="button"
                                        data-testid={`unblock-upi-${r.id}`}
                                        onClick={() => unblock(r)}
                                        className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-2.5 py-1 text-xs font-semibold text-slate-700 transition hover:border-rose-300 hover:bg-rose-50 hover:text-rose-700"
                                    >
                                        <Trash size={13} /> Unblock
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
